import { maybeParseCalendarDate } from '@rematter/common/dateTime';
import { parseZonedDateTime } from '@rematter/common/dateTime';
import {
  enumWeightDisplayUnit,
  PurchaseOrderStatus,
} from '@rematter/frontend-lib-api-client';

import { PurchaseOrderRecord } from '../types/purchaseOrder';
import { toMoney } from '../utils/money';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';

export const purchaseOrderDetailQueryAtom = createQueryAtomFactory(
  'trade.purchaseOrderDetail',
  (params: { id: string }) => ({
    purchaseOrders: {
      __args: {
        filter: {
          ids: [params.id],
          includeLineItems: true,
        },
      },
      entries: {
        id: true,
        documentNumber: true,
        status: true,
        startDate: true,
        endDate: true,
        counterpartyReferenceNumber: true,
        paymentCurrency: true,
        freightTerm: true,
        paymentTerm: true,
        notes: true,
        completionNotes: true,
        lineItems: {
          id: true,
          unit: true,
          quantity: true,
          allocatedQuantity: true,
          receivedQuantity: true,
          allocatedPercentage: true,
          fulfilledPercentage: true,
          unitPrice: {
            amount: true,
            currencyCode: true,
          },
          totalPrice: {
            amount: true,
            currencyCode: true,
          },
          timestamps: {
            createdAt: true,
            updatedAt: true,
          },
          item: {
            id: true,
            name: true,
            description: true,
          },
        },
        timestamps: {
          createdAt: true,
          updatedAt: true,
        },
        account: {
          id: true,
          name: true,
          customAttributes: true,
        },
        contact: {
          id: true,
          fullName: true,
        },
        facility: {
          id: true,
          facilityName: true,
        },
      },
    },
  }),
  {
    select: (data): PurchaseOrderRecord => {
      const purchaseOrder = data.purchaseOrders.entries[0];
      return {
        ...purchaseOrder,
        status: purchaseOrder.status as PurchaseOrderStatus,
        startDate: maybeParseCalendarDate(purchaseOrder.startDate),
        endDate: maybeParseCalendarDate(purchaseOrder.endDate),
        timestamps: {
          createdAt: parseZonedDateTime(purchaseOrder.timestamps.createdAt),
          updatedAt: parseZonedDateTime(purchaseOrder.timestamps.updatedAt),
        },
        // Todo: include Weight DIsplay Unit from API when available
        weightDisplayUnit: enumWeightDisplayUnit.PriceUnit,
        lineItems: purchaseOrder.lineItems?.map((lineItem) => ({
          ...lineItem,
          unitPrice: toMoney(lineItem.unitPrice),
          totalPrice: toMoney(lineItem.totalPrice),
          itemId: lineItem.item?.id ?? null,
          item: lineItem.item ?? null,
          allocatedQuantity: lineItem.allocatedQuantity ?? 0,
          receivedQuantity: lineItem.receivedQuantity ?? 0,
          allocatedPercentage: lineItem.allocatedPercentage ?? null,
          fulfilledPercentage: lineItem.fulfilledPercentage ?? null,
        })),
        accountRepresentative: null,
        accountAddress: null,
      };
    },
  },
);
